import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToolkitEek2ResultsComponent } from '../../components/toolkit/eek2-results/eek2-results.component';
import { ToolkitSurveyResultsComponent } from '../../components/toolkit/survey-results/survey-results.component';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import RoutingService from '../../services/routing.service';
import ToolkitService from '../../services/toolkit.service';
import Toast from '../../services/toast.service';
import DictionaryService from '../../services/dictionary.service';
import { localizeToolkitContent } from '../../utils/toolkit-content-localization.util';
import { parseJsonLenient } from '../../utils/toolkit-json.util';

@Component({
	standalone: true,
	selector: 'assigned-toolkit-resource',
	templateUrl: './assigned-toolkit-resource.component.html',
	imports: [CommonModule, FormsModule, TranslatePipe, LoaderOverlayComponent, ToolkitEek2ResultsComponent, ToolkitSurveyResultsComponent]
})
export default class AssignedToolkitResourceComponent implements OnInit {
	private routingService: RoutingService = inject(RoutingService);
	private route: ActivatedRoute = inject(ActivatedRoute);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private toast: Toast = inject(Toast);
	private dictionaryService: DictionaryService = inject(DictionaryService);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		// Regular-user page.
		this.routingService.validateAccess('myExercises').then(() => this.onResolve());
	}

	onResolve() {
		this.vm.loading = true;
		this.vm.error = false;
		this.vm.kind = null;

		const kindParam = (this.route.snapshot.paramMap.get('kind') || '').toString().toLowerCase();
		this.vm.assignmentId = (this.route.snapshot.paramMap.get('assignmentId') || '').toString();
		this.vm.returnTo = (this.route.snapshot.queryParamMap.get('return_to') || '/my-exercises').toString();

		this.vm.close = this.close.bind(this);

		// Exercise state
		this.vm.assignment = null;
		this.vm.assignmentNote = null;
		this.vm.item = null; // resource
		this.vm.themes = [];
		this.vm.theme = null;
		this.vm.content = null;
		this.vm.snapshotContent = null;
		this.vm.contentParseError = false;
		this.vm.response = {};
		this.vm.blocks = [];
		this.vm.submitting = false;
		this.vm.isReadOnly = false;
		this.vm.canRepeatExercise = false;

		// Survey state
		this.vm.assessmentAssignment = null;
		this.vm.assessment = null;
		this.vm.questions = null;
		this.vm.rawQuestions = null;
		this.vm.scoring = null;
		this.vm.step = 0;
		this.vm.answers = [];
		this.vm.isSurveyReadOnly = false;
		this.vm.isEek2Survey = false;
		this.vm.showSurveyResults = false;

		this.vm.renderFieldByType = this.renderFieldByType.bind(this);
		this.vm.fieldMidValue = this.fieldMidValue.bind(this);
		this.vm.selectChip = this.selectChip.bind(this);
		this.vm.isChipSelected = this.isChipSelected.bind(this);
		this.vm.submitExercise = this.submitExercise.bind(this);
		this.vm.repeatExercise = this.repeatExercise.bind(this);
		this.vm.resourceFormatLabel = this.resourceFormatLabel.bind(this);

		this.vm.prev = this.prev.bind(this);
		this.vm.next = this.next.bind(this);
		this.vm.selectAnswer = this.selectAnswer.bind(this);
		this.vm.submitSurvey = this.submitSurvey.bind(this);
		this.vm.openEek2History = this.openEek2History.bind(this);
		this.vm.progressPercent = this.progressPercent.bind(this);
		this.vm.totalQuestions = this.totalQuestions.bind(this);
		this.vm.currentItem = this.currentItem.bind(this);

		if (!this.vm.assignmentId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		// Prefer explicit kind from route to avoid id collisions between assignment tables.
		if (kindParam === 'exercise') {
			this.loadExerciseAssignmentOnly();
			return;
		}
		if (kindParam === 'survey' || kindParam === 'assessment') {
			this.loadSurveyAssignmentOnly();
			return;
		}

		// Backwards/compat: resolve assignment type by attempting the two /me endpoints.
		this.toolkitService.getMyAssignment(
			{ id: this.vm.assignmentId },
			(a) => {
				this.vm.kind = 'exercise';
				this.vm.assignment = a || null;
				this.vm.assignmentNote = a?.note || null;
				const completions = Array.isArray(a?.completions) ? a.completions : [];
				this.vm.isReadOnly = completions.length > 0;
				this.vm.canRepeatExercise = completions.length > 0;
				this.loadExercise();
			},
			() => this.loadSurveyAssignmentOnly()
		);
	}

	private loadExerciseAssignmentOnly(onError?: () => void) {
		this.toolkitService.getMyAssignment(
			{ id: this.vm.assignmentId },
			(a) => {
				this.vm.kind = 'exercise';
				this.vm.assignment = a || null;
				this.vm.assignmentNote = a?.note || null;
				const completions = Array.isArray(a?.completions) ? a.completions : [];
				this.vm.isReadOnly = completions.length > 0;
				this.vm.canRepeatExercise = completions.length > 0;
				// If already completed, preload the latest submitted answers so the form shows what the user entered.
				this.preloadExerciseResponseFromCompletions(completions);
				this.loadExercise();
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
				if (onError) onError();
			}
		);
	}

	private loadSurveyAssignmentOnly(onError?: () => void) {
		this.toolkitService.getMyAssessmentAssignment(
			{ id: this.vm.assignmentId },
			(a) => {
				this.vm.kind = 'survey';
				this.vm.assessmentAssignment = a || null;
				this.vm.assignmentNote = a?.note || null;
				const status = (a?.status || '').toString().toLowerCase();
				this.vm.isSurveyReadOnly = status === 'completed' || !!a?.completedAt;
				this.loadSurvey();
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
				if (onError) onError();
			}
		);
	}

	private loadExercise() {
		const resourceId = (this.vm.assignment?.resourceId || '').toString();
		if (!resourceId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		this.toolkitService.getCatalogue({}, (catalogue) => {
			this.vm.themes = Array.isArray(catalogue?.themes) ? catalogue.themes : [];
			this.attachTheme();
			this.buildExerciseBlocks();
		});

		this.toolkitService.getResource(
			{ id: resourceId },
			(item) => {
				this.vm.item = this.withDictionaryTextFallbacks(item || null);
				this.attachTheme();
				// Keep the assignment snapshot as a fallback only.
				// For rendering, prefer the live resource payload because it carries the localized exercise content,
				// matching how surveys prefer localized questionsJson over the snapshot copy.
				if (this.vm.assignment?.resourceContentSnapshotJson) {
					try {
						this.vm.snapshotContent = parseJsonLenient(this.vm.assignment.resourceContentSnapshotJson);
					} catch {}
				}
				this.parseExerciseContent();
				this.buildExerciseBlocks();
				this.vm.loading = false;
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
			}
		);
	}

	private preloadExerciseResponseFromCompletions(completions: any[]) {
		if (!Array.isArray(completions) || completions.length === 0) return;
		const sorted = completions
			.slice()
			.sort((x, y) => {
				const tx = x?.submittedAt ? new Date(x.submittedAt).getTime() : x?.created ? new Date(x.created).getTime() : 0;
				const ty = y?.submittedAt ? new Date(y.submittedAt).getTime() : y?.created ? new Date(y.created).getTime() : 0;
				return ty - tx;
			});
		const latest = sorted[0];
		const raw = latest?.responseJson || latest?.response_json || null;
		if (!raw) return;
		try {
			const parsed = parseJsonLenient(raw);
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				this.vm.response = { ...(this.vm.response || {}), ...(parsed as any) };
			}
		} catch {
			// ignore
		}
	}

	private loadSurvey() {
		const assessmentId = (this.vm.assessmentAssignment?.assessmentId || '').toString();
		if (!assessmentId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		this.toolkitService.getAssessment(
			{ id: assessmentId },
			(a) => {
				this.vm.assessment = a || null;
				this.parseSurveyPayloads();
				this.vm.loading = false;
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
			}
		);
	}

	close() {
		this.angularRoutingHelperService.setPath(this.vm.returnTo || '/my-exercises', false, false);
	}

	openEek2History() {
		if (!this.vm.assignmentId) return;
		this.angularRoutingHelperService.setPath(
			`/my-exercises/resource/survey/${this.vm.assignmentId}/history`,
			false,
			false,
			{ return_to: `/my-exercises/resource/survey/${this.vm.assignmentId}` }
		);
	}

	/* ===================== Exercise Rendering (based on toolkit-preview) ===================== */
	private attachTheme() {
		if (!this.vm.item || !Array.isArray(this.vm.themes)) return;
		const themeId = this.vm.item?.themeId || this.vm.item?.theme_id || this.vm.item?.theme;
		this.vm.theme = this.vm.themes.find((t) => (t?.id || t?.themeId) === themeId) || null;
	}

	resourceFormatLabel(format: string) {
		const key = (format || '').toString().trim().toLowerCase();
		switch (key) {
			case 'worksheet':
				return 'Toolkit.Format.Worksheet';
			case 'planner':
				return 'Toolkit.Format.Planner';
			case 'exercise':
			default:
				return 'Toolkit.Format.Exercise';
		}
	}

	private isDynamicFormContent(content: any): boolean {
		return !!(content && typeof content === 'object' && Array.isArray((content as any).fields));
	}

	private parseExerciseContent() {
		// If content is already prepared by this view, keep it.
		if (this.vm.content && typeof this.vm.content === 'object') {
			this.vm.content = localizeToolkitContent(this.vm.content, this.currentLocale());
			this.vm.content = this.withDictionaryTextFallbacks(this.vm.content);
			if (this.isDynamicFormContent(this.vm.content)) {
				this.vm.content = this.parseDynamicFormContent(this.vm.content);
			}
			this.reconcileCompletedSelectResponsesWithLocalizedOptions();
			return;
		}

		const raw = this.vm.item?.contentJson || this.vm.item?.content_json || null;
		const resourceContent = raw ? parseJsonLenient(raw) : null;
		if (resourceContent) {
			const localized = localizeToolkitContent(resourceContent, this.currentLocale());
			const translated = this.withDictionaryTextFallbacks(localized);
			this.vm.content = this.isDynamicFormContent(translated) ? this.parseDynamicFormContent(translated) : translated;
			this.reconcileCompletedSelectResponsesWithLocalizedOptions();
			return;
		}

		if (this.vm.snapshotContent && typeof this.vm.snapshotContent === 'object') {
			const localizedSnapshot = localizeToolkitContent(this.vm.snapshotContent, this.currentLocale());
			const translatedSnapshot = this.withDictionaryTextFallbacks(localizedSnapshot);
			this.vm.content = this.isDynamicFormContent(translatedSnapshot)
				? this.parseDynamicFormContent(translatedSnapshot)
				: translatedSnapshot;
			this.reconcileCompletedSelectResponsesWithLocalizedOptions();
			return;
		}

		this.vm.content = null;
		this.vm.contentParseError = true;
	}

	private parseDynamicFormContent(raw: any): any | null {
		try {
			const parsed = this.withDictionaryTextFallbacks(localizeToolkitContent(parseJsonLenient(raw), this.currentLocale()));
			if (!this.isDynamicFormContent(parsed)) return null;
			return {
				instructions: (parsed.instructions || '').toString(),
				fields: Array.isArray(parsed.fields) ? parsed.fields : []
			};
		} catch (e) {
			return null;
		}
	}

	private withDictionaryTextFallbacks(value: any): any {
		if (Array.isArray(value)) return value.map((item) => this.withDictionaryTextFallbacks(item));
		if (!value || typeof value !== 'object') return this.translateResourceText(value);

		const out: any = {};
		Object.keys(value).forEach((key) => {
			if (key === 'options' && Array.isArray(value[key])) {
				out[key] = value[key].map((option) => this.withDictionaryTextFallbacks(option));
				return;
			}
			if (this.isTranslatableResourceTextKey(key)) {
				out[key] = this.translateResourceText(value[key]);
				return;
			}
			out[key] = this.withDictionaryTextFallbacks(value[key]);
		});
		return out;
	}

	private isTranslatableResourceTextKey(key: string): boolean {
		return [
			'title',
			'name',
			'summary',
			'description',
			'instructions',
			'label',
			'placeholder',
			'html',
			'text',
			'hint'
		].includes((key || '').toString());
	}

	private translateResourceText(value: any): any {
		if (typeof value !== 'string') return value;
		const suffix = this.dictionaryKeySuffix(value);
		if (!suffix) return value;
		const key = `Toolkit.ResourceText.${suffix}`;
		const translated = this.dictionaryService.translate(key);
		return translated && translated !== key ? translated : value;
	}

	private dictionaryKeySuffix(value: string): string {
		return (value || '')
			.toString()
			.trim()
			.replace(/<[^>]*>/g, ' ')
			.replace(/&[^;\s]+;/g, ' ')
			.replace(/[^A-Za-z0-9]+(.)/g, (_match, chr) => chr.toUpperCase())
			.replace(/^[a-z]/, (chr) => chr.toUpperCase());
	}

	private optionLabel(option: any): string {
		return this.optionLabelForLocale(option, this.currentLocale(), true);
	}

	private optionLabelForLocale(option: any, locale: string, shouldLocalize: boolean): string {
		if (option == null) return '';
		if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
			return option.toString();
		}
		const localized = shouldLocalize ? localizeToolkitContent(option, locale) : option;
		return (
			localized?.label ||
			localized?.text ||
			localized?.title ||
			localized?.name ||
			option?.label ||
			option?.text ||
			option?.defaultText ||
			option?.value ||
			''
		).toString();
	}

	private optionStableKey(option: any): string {
		if (option == null) return '';
		if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
			return option.toString();
		}
		return (
			option?.value ??
			option?.id ??
			option?.key ??
			option?.code ??
			option?.name ??
			option?.defaultText ??
			option?.label ??
			option?.text ??
			''
		).toString();
	}

	private describeOptions(
		options: any[],
		locale: string,
		shouldLocalize: boolean
	): Array<{ label: string; key: string }> {
		return (options || []).map((option) => ({
			label: this.optionLabelForLocale(option, locale, shouldLocalize),
			key: this.optionStableKey(option)
		}));
	}

	private chipOption(option: any): { label: string; hint: string; key: string } {
		if (option == null) return { label: '', hint: '', key: '' };
		if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
			const value = option.toString();
			return { label: value, hint: '', key: value };
		}
		const localized = localizeToolkitContent(option, this.currentLocale());
		return {
			label: (
				localized?.label ||
				localized?.text ||
				option?.label ||
				option?.text ||
				option?.value ||
				''
			).toString(),
			hint: (
				localized?.hint ||
				localized?.description ||
				option?.hint ||
				option?.description ||
				''
			).toString(),
			key: this.optionStableKey(option)
		};
	}

	private normalizeOptions(options: any[]): string[] {
		const out: string[] = [];
		(options || []).forEach((o) => {
			if (o == null) return;
			if (typeof o === 'string' || typeof o === 'number' || typeof o === 'boolean') {
				const raw = o.toString();
				const parsed = parseJsonLenient(raw);
				if (Array.isArray(parsed)) {
					this.normalizeOptions(parsed).forEach((item) => out.push(item));
					return;
				}
				if (raw.includes('","')) {
					raw
						.split('","')
						.map((item) => item.replace(/^"/, '').replace(/"$/, '').trim())
						.filter((item) => !!item)
						.forEach((item) => out.push(item));
					return;
				}
				out.push(raw);
				return;
			}
			// Backend validator allows object options by matching submitted value against option.label.
			if (typeof o === 'object' && (o as any).label != null) {
				const raw = this.optionLabel(o);
				out.push(raw);
				return;
			}
			const localized = this.optionLabel(o);
			const raw = localized || o.toString();
			out.push(raw);
		});
		return out;
	}

	private findFieldById(fields: any[], fieldId: string): any | null {
		if (!Array.isArray(fields) || !fieldId) return null;
		return fields.find((field) => field && (field.id || field.key) === fieldId) || null;
	}

	private reconcileCompletedSelectResponsesWithLocalizedOptions() {
		const response = this.vm.response;
		const currentFields = Array.isArray(this.vm.content?.fields) ? this.vm.content.fields : [];
		const snapshotFields = Array.isArray(this.vm.snapshotContent?.fields) ? this.vm.snapshotContent.fields : [];
		if (!response || typeof response !== 'object' || !currentFields.length || !snapshotFields.length) return;

		currentFields.forEach((field) => {
			if (!field) return;
			const fieldType = (field.type || '').toString();
			if (fieldType !== 'select' && fieldType !== 'chips') return;
			const fieldId = field.id || field.key;
			if (!fieldId) return;

			const savedValue = response[fieldId];
			if (savedValue === undefined || savedValue === null || savedValue === '') return;

			const currentOptions = this.describeOptions(Array.isArray(field.options) ? field.options : [], this.currentLocale(), true);
			const savedValueString = savedValue.toString();
			if (currentOptions.some((option) => option.label === savedValueString || option.key === savedValueString)) return;

			const snapshotField = this.findFieldById(snapshotFields, fieldId);
			if (!snapshotField) return;

			const snapshotOptions = this.describeOptions(Array.isArray(snapshotField.options) ? snapshotField.options : [], this.currentLocale(), false);
			const matchedIndex = snapshotOptions.findIndex((option) => option.label === savedValueString || option.key === savedValueString);
			if (matchedIndex < 0 || matchedIndex >= currentOptions.length) return;

			response[fieldId] = currentOptions[matchedIndex].label;
		});
	}

	renderFieldByType(field: any): { kind: string; placeholder?: string; options?: string[]; chipOptions?: { label: string; hint: string; key: string }[] } {
		const t = (field?.type || 'text').toString();
		if (t === 'subheader') return { kind: 'subheader' };
		if (t === 'select') {
			const rawOptions = Array.isArray(field?.options)
				? field.options
				: typeof field?.options === 'string'
					? [field.options]
					: [];
			return { kind: 'select', options: this.normalizeOptions(rawOptions) };
		}
		if (t === 'chips') {
			const rawOptions = Array.isArray(field?.options) ? field.options : [];
			return { kind: 'chips', chipOptions: rawOptions.map((option) => this.chipOption(option)) };
		}
		return { kind: t, placeholder: field?.placeholder || '' };
	}

	fieldMidValue(field: any): number {
		const min = Number(field?.min ?? 0);
		const max = Number(field?.max ?? 10);
		if (!isFinite(min) || !isFinite(max)) return 0;
		return Math.round((min + max) / 2);
	}

	selectChip(fieldId: string, option: any, event?: Event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		if (this.vm.isReadOnly || !fieldId) return;
		if (!this.vm.response || typeof this.vm.response !== 'object') {
			this.vm.response = {};
		}
		this.vm.response[fieldId] = (option?.key ?? option?.label ?? option ?? '').toString();
	}

	isChipSelected(fieldId: string, option: any): boolean {
		if (!fieldId || !this.vm.response) return false;
		const selected = (this.vm.response[fieldId] || '').toString().trim();
		const label = (option?.label ?? option ?? '').toString().trim();
		const key = (option?.key ?? '').toString().trim();
		return selected === label || (!!key && selected === key);
	}

	private buildExerciseBlocks() {
		if (!this.vm.item) return;

		// Defaults for range fields.
		if (this.vm.content && this.vm.content.fields && typeof this.vm.response === 'object' && this.vm.response) {
			(this.vm.content.fields || []).forEach((f) => {
				if (!f || f.type === 'subheader') return;
				const id = f.id || f.key;
				if (!id) return;
				if (f.type === 'range' && (this.vm.response[id] === undefined || this.vm.response[id] === null || this.vm.response[id] === '')) {
					this.vm.response[id] = this.fieldMidValue(f);
				}
			});
		}

		const blocks: any[] = [];

		const format = (this.vm.item?.format || '').toString().trim().toLowerCase();
		if (format === 'psychoeducation') {
			blocks.push({ type: 'psychoeducation' });
			const reflections = (this.vm.content && this.vm.content.reflections) || [];
			if (Array.isArray(reflections) && reflections.length) {
				blocks.push({
					type: 'reflections',
					questions: reflections.map((q) => {
						if (typeof q === 'string') return q;
						const localized = localizeToolkitContent(q, this.currentLocale());
						return localized?.text || localized?.label || localized?.title || q?.defaultText || q?.toString?.() || '';
					})
				});
			}
			blocks.push({ type: 'cta_complete' });
		} else {
			// Dynamic form renderer: requires "fields" array in content.
			if (this.vm.content && this.vm.content.fields) {
				blocks.push({ type: 'interactive' });
				blocks.push({ type: 'cta_complete' });
			} else {
				blocks.push({ type: 'unsupported_content' });
			}
		}

		this.vm.blocks = blocks;
	}

	private isBlankValue(value: any): boolean {
		return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
	}

	private normalizeExerciseValue(field: any, value: any): any {
		const type = (field?.type || '').toString();
		if (typeof value === 'string') {
			value = value.trim();
		}
		if ((type === 'range' || type === 'number') && value !== undefined && value !== null && value !== '') {
			const n = Number(value);
			return isFinite(n) ? n : value;
		}
		if ((type === 'select' || type === 'chips') && value !== undefined && value !== null) {
			return value.toString();
		}
		return value;
	}

	private mapChoiceValueToSnapshotOption(fieldId: string, value: any): any {
		if (value === undefined || value === null || value === '') return value;

		const snapshotFields = Array.isArray(this.vm.snapshotContent?.fields) ? this.vm.snapshotContent.fields : [];
		const currentFields = Array.isArray(this.vm.content?.fields) ? this.vm.content.fields : [];
		const snapshotField = this.findFieldById(snapshotFields, fieldId);
		if (!snapshotField) return value;

		const snapshotOptions = this.describeOptions(Array.isArray(snapshotField.options) ? snapshotField.options : [], this.currentLocale(), false);
		if (!snapshotOptions.length) return value;

		const valueString = value.toString();
		const directSnapshotMatch = snapshotOptions.find((option) => option.label === valueString || option.key === valueString);
		if (directSnapshotMatch) return directSnapshotMatch.label;

		const currentField = this.findFieldById(currentFields, fieldId);
		const currentOptions = this.describeOptions(Array.isArray(currentField?.options) ? currentField.options : [], this.currentLocale(), true);
		const currentIndex = currentOptions.findIndex((option) => option.label === valueString || option.key === valueString);
		if (currentIndex < 0 || currentIndex >= snapshotOptions.length) return valueString;

		return snapshotOptions[currentIndex].label;
	}

	private getSubmissionExerciseFields(): any[] {
		const snapshotFields = Array.isArray(this.vm.snapshotContent?.fields) ? this.vm.snapshotContent.fields : null;
		if (snapshotFields && snapshotFields.length) return snapshotFields;
		const contentFields = Array.isArray(this.vm.content?.fields) ? this.vm.content.fields : null;
		return contentFields || [];
	}

	private isOtherSelection(value: any): boolean {
		return typeof value === 'string' && value.toLowerCase().includes('other');
	}

	private findOtherCompanionField(fields: any[], fieldId: string): any | null {
		if (!fieldId) return null;
		return fields.find((candidate) => candidate && (candidate.id || candidate.key) === fieldId + '_other') || null;
	}

	private validateExerciseResponse(fields: any[], response: any): boolean {
		for (const field of fields || []) {
			if (!field || field.type === 'subheader') continue;
			const id = field.id || field.key;
			if (!id) continue;
			const value = response[id];

			if (field.required === true && this.isBlankValue(value)) {
				this.toast.show((field.label || id).toString());
				return false;
			}

			if ((field.type || '').toString() === 'select' && this.isOtherSelection(value)) {
				const otherField = this.findOtherCompanionField(fields, id);
				const otherId = otherField?.id || otherField?.key;
				const otherValue = otherId ? response[otherId] : undefined;
				if (otherField && this.isBlankValue(otherValue)) {
					this.toast.show((otherField.label || otherId).toString());
					return false;
				}
			}
		}
		return true;
	}

	private collectExerciseResponse(): any {
		const out: any = {};
		const fields: any[] = this.getSubmissionExerciseFields();
		if (!this.validateExerciseResponse(fields, this.vm.response || {})) {
			return null;
		}
		if (Array.isArray(fields)) {
			fields.forEach((f) => {
				if (!f || f.type === 'subheader') return;
				const id = f.id || f.key;
				if (!id) return;
				const rawValue = this.vm.response[id];
				let normalizedValue = this.normalizeExerciseValue(f, rawValue);
				const fieldType = (f?.type || '').toString();
				if (fieldType === 'select' || fieldType === 'chips') {
					normalizedValue = this.mapChoiceValueToSnapshotOption(id, normalizedValue);
				}

				if (fieldType === 'select' && !this.isOtherSelection(normalizedValue)) {
					const otherField = this.findOtherCompanionField(fields, id);
					const otherId = otherField?.id || otherField?.key;
					if (otherId) {
						this.vm.response[otherId] = '';
					}
				}

				if (!this.isBlankValue(normalizedValue)) {
					out[id] = normalizedValue;
				}
			});
		}
		const reflections: any[] = (this.vm.content && this.vm.content.reflections) || [];
		if (Array.isArray(reflections)) {
			reflections.forEach((q, i) => {
				const key = 'reflection_' + i;
				const value = this.vm.response[key];
				if (!this.isBlankValue(value)) out[key] = value;
			});
		}
		return out;
	}

	submitExercise() {
		if (this.vm.isReadOnly) return;
		if (!this.vm.assignmentId) {
			this.toast.showWithTranslation('Toolkit.Preview.NoAssignment');
			return;
		}
		const response = this.collectExerciseResponse();
		console.log(response);
		if (!response) return;
		this.vm.submitting = true;
		this.toolkitService.submitMyCompletion(
			{ assignmentId: this.vm.assignmentId },
			{ response: response, clientLocale: null },
			() => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Preview.Completed');
				this.vm.isReadOnly = true;
				this.vm.canRepeatExercise = true;
				this.close();
			},
			(err) => {
				this.vm.submitting = false;
				const message = typeof err?.error === 'string' && err.error.trim() ? err.error.trim() : null;
				if (message) {
					this.toast.show(message);
				} else {
					this.toast.showWithTranslation('Toolkit.Preview.CompleteError');
				}
			}
		);
	}

	repeatExercise() {
		if (!this.vm.canRepeatExercise) return;
		this.vm.isReadOnly = false;
		this.vm.response = {};
		this.buildExerciseBlocks();
		window.scrollTo(0, 0);
	}

	totalQuestions(): number {
		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		return items.length;
	}

	currentItem(): any | null {
		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		return items[this.vm.step] || null;
	}

	progressPercent(): number {
		const total = this.totalQuestions();
		if (!total) return 0;
		return ((this.vm.step + 1) / total) * 100;
	}

	private parseSurveyPayloads() {
		const a = this.vm.assessment;
		if (!a) return;

		const localizedQuestionsRaw = a.questionsJson || a.questions_json || null;
		const snapshotQuestionsRaw = this.vm.assessmentAssignment?.assessmentQuestionsSnapshotJson || null;
		const qRaw = localizedQuestionsRaw || snapshotQuestionsRaw || null;
		const sRaw =
			this.vm.assessmentAssignment?.assessmentScoringSnapshotJson || a.scoringJson || a.scoring_json || null;

		const questions = parseJsonLenient(qRaw);
		const scoring = parseJsonLenient(sRaw);

		this.vm.rawQuestions = questions && typeof questions === 'object' ? questions : null;
		this.vm.isEek2Survey = this.isEek2Assessment(this.vm.rawQuestions, a);
		this.vm.questions = this.normalizeSurveyQuestions(this.vm.rawQuestions);
		this.vm.scoring = scoring && typeof scoring === 'object' ? scoring : null;

		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		this.vm.answers = new Array(items.length).fill(undefined);

		const rawAnswers = this.vm.assessmentAssignment?.answersJson || this.vm.assessmentAssignment?.answers_json || null;
		if (rawAnswers) {
			try {
				const parsedAnswers = parseJsonLenient(rawAnswers);
				if (Array.isArray(parsedAnswers)) this.vm.answers = parsedAnswers.slice(0, items.length);
			} catch {}
		}

		this.vm.showSurveyResults = this.vm.isSurveyReadOnly && this.hasCompletedSurveyAnswers();
		this.vm.step = 0;
	}

	private isEek2Assessment(questions: any, assessment: any): boolean {
		const instrument = questions?.instrument || null;
		const code = (instrument?.code || questions?.code || assessment?.code || assessment?.title || '').toString().trim().toLowerCase();
		const id = (instrument?.id || '').toString().trim().toLowerCase();
		return id === 'eek2' || code === 'eek-2' || code === 'eek2';
	}

	private normalizeSurveyQuestions(rawQuestions: any): any | null {
		if (!rawQuestions || typeof rawQuestions !== 'object') return null;
		if (Array.isArray(rawQuestions?.items)) return rawQuestions;

		const instrument = rawQuestions?.instrument;
		if (!instrument || !Array.isArray(instrument?.items)) return rawQuestions;

		return {
			...rawQuestions,
			code: instrument?.code || rawQuestions?.code || '',
			title: instrument?.title || rawQuestions?.title || '',
			fullTitle: instrument?.fullTitle || instrument?.title || rawQuestions?.fullTitle || rawQuestions?.title || '',
			description: instrument?.description || instrument?.descriptionEt || '',
			timeframeText: instrument?.timeframeText || instrument?.timeframeTextEt || '',
			itemCount: instrument?.itemCount || instrument.items.length,
			responseScale: Array.isArray(instrument?.responseScale)
				? instrument.responseScale.map((option) => ({
					value: option?.value,
					label: option?.label || option?.labelEt || ''
				}))
				: [],
			items: instrument.items
				.slice()
				.sort((a, b) => (a?.order || 0) - (b?.order || 0))
				.map((item) => ({
					...item,
					text: item?.text || item?.textEt || ''
				}))
		};
	}

	private hasCompletedSurveyAnswers(): boolean {
		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		if (!items.length || !Array.isArray(this.vm.answers) || this.vm.answers.length < items.length) return false;
		return items.every((_: any, index: number) => this.vm.answers[index] !== undefined && this.vm.answers[index] !== null);
	}

	private currentLocale(): string {
		return (this.dictionaryService.getCurrentLocale() || '').toString();
	}

	selectAnswer(value: any) {
		if (this.vm.submitting) return;
		if (this.vm.isSurveyReadOnly) return;
		this.vm.answers[this.vm.step] = value;
	}

	prev() {
		if (this.vm.step > 0) {
			this.vm.step--;
			window.scrollTo(0, 0);
		}
	}

	next() {
		if (this.vm.step < this.totalQuestions() - 1) {
			this.vm.step++;
			window.scrollTo(0, 0);
		}
	}

	submitSurvey() {
		if (this.vm.isSurveyReadOnly) {
			this.close();
			return;
		}
		if (!this.vm.assignmentId) {
			this.toast.showWithTranslation('Toolkit.Assessment.Preview.NoAssignment');
			return;
		}

		const unanswered = (this.vm.answers || []).filter((x) => x === undefined || x === null).length;
		if (unanswered > 0) {
			this.toast.showWithTranslation('Toolkit.Assessment.Preview.Incomplete');
			return;
		}

		this.vm.submitting = true;
		this.toolkitService.submitMyAssessmentAnswers(
			{ assignmentId: this.vm.assignmentId },
			{ answers: this.vm.answers },
			(updated) => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Assessment.Preview.Submitted');
				if (updated) this.vm.assessmentAssignment = updated;
				this.vm.isSurveyReadOnly = true;
				this.vm.showSurveyResults = true;
				window.scrollTo(0, 0);
			},
			() => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Assessment.Preview.SubmitError');
			}
		);
	}
}
